import { convertToCommonErrors, convertToCommonErrorsUsingErrorsMessages } from '../../common/utils/errorUtils.js';
import { TypeKeys, generateActionId } from './actionsUtils.js';
import type { CrudAction, DisplayItemsLoadAction, ErrorAction, ErrorActionCreator } from './actionsUtils.js';
import type { TerminateType } from '../../common/enums.js';
import type { CommonError } from '../../common/types/errors.js';
import type { PurposeOfUseOrContact } from '../../common/types/subscription.js';
import type {
  Address,
  Contact,
  GenericError,
  GenericErrorDuplicateContact,
  PostEppRedeemTerminateRequest,
  PutContactResponse,
  Subscription,
  SubscriptionAction,
  SubscriptionActionSearchResponse,
  SubscriptionActionsResponse,
  SubscriptionChangeUserInformationRequest,
  SubscriptionPbxConfiguration,
} from '../../generated/api/models.js';
import type { ItemsQuery } from '../common/store.js';
import type { Action } from 'redux';

export interface TerminateSubscriptionAction extends CrudAction<TypeKeys.TERMINATE_SUBSCRIPTION> {
  subscriptionId: string;
  effectiveDate: number;
  donateNumber?: boolean;
  terminateType?: TerminateType;
}

export const terminateSubscriptionAction = (
  subscriptionId: string,
  effectiveDate: number,
  donateNumber?: boolean,
  terminateType?: TerminateType
): TerminateSubscriptionAction => ({
  donateNumber,
  effectiveDate,
  terminateType,
  id: generateActionId(),
  subscriptionId,
  type: TypeKeys.TERMINATE_SUBSCRIPTION,
});

export interface TerminateSubscriptionActionFulfilled {
  type: TypeKeys.TERMINATE_SUBSCRIPTION_FULFILLED;
  subscriptionAction: SubscriptionAction;
}

export const terminateSubscriptionActionFulfilled = (subscriptionAction: SubscriptionAction) => ({
  subscriptionAction,
  type: TypeKeys.TERMINATE_SUBSCRIPTION_FULFILLED,
});

export interface TerminateSubscriptionActionFailed extends ErrorAction<TypeKeys.TERMINATE_SUBSCRIPTION_FAILED> {
  type: TypeKeys.TERMINATE_SUBSCRIPTION_FAILED;
}

export const terminateSubscriptionActionFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): TerminateSubscriptionActionFailed => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.TERMINATE_SUBSCRIPTION_FAILED,
});

export interface ShowSuspendSubscriptionOptions {
  type: TypeKeys.SHOW_SUSPEND_SUBSCRIPTION_OPTIONS;
}

export const showSuspendSubscriptionOptions = (): ShowSuspendSubscriptionOptions => ({
  type: TypeKeys.SHOW_SUSPEND_SUBSCRIPTION_OPTIONS,
});

export interface CloseSuspendSubscriptionOptions {
  type: TypeKeys.CLOSE_SUSPEND_SUBSCRIPTION_OPTIONS;
}

export const closeSuspendSubscriptionOptions = (): CloseSuspendSubscriptionOptions => ({
  type: TypeKeys.CLOSE_SUSPEND_SUBSCRIPTION_OPTIONS,
});

export interface ShowTerminateSubscriptionForm {
  type: TypeKeys.SHOW_TERMINATE_SUBSCRIPTION_FORM;
}

export const showTerminateSubscriptionForm = (): ShowTerminateSubscriptionForm => ({
  type: TypeKeys.SHOW_TERMINATE_SUBSCRIPTION_FORM,
});

export interface CloseTerminateSubscriptionForm {
  type: TypeKeys.CLOSE_TERMINATE_SUBSCRIPTION_FORM;
}

export const closeTerminateSubscriptionForm = (): CloseTerminateSubscriptionForm => ({
  type: TypeKeys.CLOSE_TERMINATE_SUBSCRIPTION_FORM,
});

export interface SuspendSubscriptionAction extends CrudAction<TypeKeys.SUSPEND_SUBSCRIPTION> {
  subscriptionId: string;
  subscriptionDisplayId: string;
}

export interface SuspendSubscriptionFulfilledAction {
  type: TypeKeys.SUSPEND_SUBSCRIPTION_FULFILLED;
  subscriptionAction: SubscriptionAction;
}

export interface SuspendSubscriptionFailedAction extends ErrorAction<TypeKeys.SUSPEND_SUBSCRIPTION_FAILED> {
  type: TypeKeys.SUSPEND_SUBSCRIPTION_FAILED;
}

export const suspendSubscription = (
  subscriptionId: string,
  subscriptionDisplayId: string
): SuspendSubscriptionAction => ({
  id: generateActionId(),
  subscriptionId,
  subscriptionDisplayId,
  type: TypeKeys.SUSPEND_SUBSCRIPTION,
});

export const suspendSubscriptionFulfilled = (subscriptionAction: SubscriptionAction) => ({
  subscriptionAction,
  type: TypeKeys.SUSPEND_SUBSCRIPTION_FULFILLED,
});

export const suspendSubscriptionFailed: ErrorActionCreator<TypeKeys.SUSPEND_SUBSCRIPTION_FAILED> = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): SuspendSubscriptionFailedAction => {
  const commonErrors = convertToCommonErrors(message, status, errors);
  return {
    errors: commonErrors,
    params,
    type: TypeKeys.SUSPEND_SUBSCRIPTION_FAILED,
  };
};

export interface ResumeSubscriptionAction extends CrudAction<TypeKeys.RESUME_SUBSCRIPTION> {
  subscriptionId: string;
}

export interface ResumeSubscriptionFulfilledAction {
  type: TypeKeys.RESUME_SUBSCRIPTION_FULFILLED;
  subscriptionAction: SubscriptionAction;
}

export interface ResumeSubscriptionFailedAction extends ErrorAction<TypeKeys.RESUME_SUBSCRIPTION_FAILED> {
  type: TypeKeys.RESUME_SUBSCRIPTION_FAILED;
  blockType?: string | undefined;
}

export const resumeSubscription = (subscriptionId: string): ResumeSubscriptionAction => ({
  id: generateActionId(),
  subscriptionId,
  type: TypeKeys.RESUME_SUBSCRIPTION,
});

export const resumeSubscriptionFulfilledAction = (subscriptionAction: SubscriptionAction) => ({
  subscriptionAction,
  type: TypeKeys.RESUME_SUBSCRIPTION_FULFILLED,
});

export enum BlockedErrorTypes {
  COLLECTION_INITIATED = 'COLLECTION_INITIATED',
  CREDIT_INITIATED = 'CREDIT_INITIATED',
}

export enum PbxErrorTypes {
  NUMBER_NOT_AVAILABLE = 'NUMBER_NOT_AVAILABLE',
}

const checkErrorTypes = (errors: CommonError[], genericErrors: GenericError[] | undefined): string => {
  if (errors && errors[0].source && errors[0].source.error && errors[0].source.error.errorType in BlockedErrorTypes) {
    return errors[0].source.error.errorType;
  } else if (genericErrors?.[0].message.includes(BlockedErrorTypes.CREDIT_INITIATED)) {
    return BlockedErrorTypes.CREDIT_INITIATED;
  } else if (genericErrors?.[0].message.includes(BlockedErrorTypes.COLLECTION_INITIATED)) {
    return BlockedErrorTypes.COLLECTION_INITIATED;
  }
  return '';
};

export const resumeSubscriptionFailedAction: ErrorActionCreator<TypeKeys.RESUME_SUBSCRIPTION_FAILED> = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): ResumeSubscriptionFailedAction => {
  const commonErrors = convertToCommonErrors(message, status, errors);
  return {
    blockType: checkErrorTypes(commonErrors, errors),
    errors: commonErrors,
    params,
    type: TypeKeys.RESUME_SUBSCRIPTION_FAILED,
  };
};

export interface EditSubscriptionSectionAction extends Action<TypeKeys.EDIT_SUBSCRIPTION_SECTION> {
  editingSection: string;
  type: TypeKeys.EDIT_SUBSCRIPTION_SECTION;
}

export const editSection = (editingSection: string): EditSubscriptionSectionAction => ({
  editingSection,
  type: TypeKeys.EDIT_SUBSCRIPTION_SECTION,
});

export interface CreateContactForSubscriptionUserInfoAction
  extends CrudAction<TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO> {
  contact: Contact;
}

export const createContactForSubscriptionUserInfo = (contact: Contact): CreateContactForSubscriptionUserInfoAction => ({
  contact,
  id: generateActionId(),
  type: TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO,
});

export interface CreateContactForSubscriptionUserInfoFulfilledAction {
  type: TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FULFILLED;
  contact: Contact;
  billingAccountContactIdRef?: string;
}

export const createContactForSubscriptionUserInfoFulfilled = (
  { contactId, lastModified }: PutContactResponse,
  contact: Contact,
  billingAccountContactIdRef?: string
): CreateContactForSubscriptionUserInfoFulfilledAction => ({
  billingAccountContactIdRef,
  contact: {
    ...contact,
    contactId,
    lastModified,
  },
  type: TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FULFILLED,
});

export type CreateContactForSubscriptionUserInfoFailedAction =
  ErrorAction<TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FAILED>;

export const createContactForSubscriptionUserInfoFailed: ErrorActionCreator<
  TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FAILED
> = (message: string, status: number, errors?: GenericError[]): CreateContactForSubscriptionUserInfoFailedAction => {
  const commonErrors = convertToCommonErrors(message, status, errors);
  return {
    errors: commonErrors,
    type: TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FAILED,
  };
};

export interface ChangeSubscriptionUserInfoAction extends CrudAction<TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO> {
  subscriptionId: string;
  purposeOfUseOrContact: PurposeOfUseOrContact;
  isCostCenterMandatory: boolean;
  isEmailMandatory: boolean;
  isPhoneNumberMandatory: boolean;
  validationErrors?: CommonError[];
  billingAccountIdForOmaLaiteLaskuRemoval?: string;
  subscriptionDisplayId?: string;
}

export type ChangeSubscriptionUserInfoPartial = [
  subscriptionId: string,
  purposeOfUseOrContact: PurposeOfUseOrContact,
  isCostCenterMandatory: boolean,
  isEmailMandatory: boolean,
  isPhoneNumberMandatory: boolean,
  validationErrors?: CommonError[],
];

export type ChangeSubscriptionUserInfoFull = [
  subscriptionId: string,
  purposeOfUseOrContact: PurposeOfUseOrContact,
  isCostCenterMandatory: boolean,
  isEmailMandatory: boolean,
  isPhoneNumberMandatory: boolean,
  validationErrors?: CommonError[],
  billingAccountIdForOmaLaiteLaskuRemoval?: string,
  subscriptionDisplayId?: string,
];

export const changeSubscriptionUserInfo = (
  ...args: ChangeSubscriptionUserInfoFull
): ChangeSubscriptionUserInfoAction => {
  const [
    subscriptionId,
    purposeOfUseOrContact,
    isCostCenterMandatory,
    isEmailMandatory,
    isPhoneNumberMandatory,
    validationErrors,
    billingAccountIdForOmaLaiteLaskuRemoval,
    subscriptionDisplayId,
  ] = args;
  return {
    billingAccountIdForOmaLaiteLaskuRemoval,
    id: generateActionId(),
    purposeOfUseOrContact,
    subscriptionId,
    subscriptionDisplayId,
    isCostCenterMandatory,
    isEmailMandatory,
    isPhoneNumberMandatory,
    validationErrors,
    type: TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO,
  };
};

export interface ChangeSubscriptionUserInfoFulfilledAction extends Action {
  parameters: SubscriptionChangeUserInformationRequest;
  subscriptionAction: SubscriptionAction;
  subscriptionId: string;
  type: TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FULFILLED;
  billingAccountIdForOmaLaiteLaskuRemoval?: string;
  subscriptionDisplayId?: string;
}

export const changeSubscriptionUserInfoFulfilled = (
  parameters: SubscriptionChangeUserInformationRequest,
  subscriptionAction: SubscriptionAction,
  subscriptionId: string,
  billingAccountIdForOmaLaiteLaskuRemoval?: string,
  subscriptionDisplayId?: string
): ChangeSubscriptionUserInfoFulfilledAction => ({
  billingAccountIdForOmaLaiteLaskuRemoval,
  parameters,
  subscriptionAction,
  subscriptionId,
  subscriptionDisplayId,
  type: TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FULFILLED,
});

export type ChangeSubscriptionUserInfoFailedAction = ErrorAction<TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FAILED>;

export const changeSubscriptionUserInfoFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): ChangeSubscriptionUserInfoFailedAction => {
  const commonErrors = convertToCommonErrorsUsingErrorsMessages(message, status, errors);
  return {
    errors: commonErrors,
    params,
    type: TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FAILED,
  };
};

export type ChangeSubscriptionUserInfoWithDuplicateFailedAction =
  ErrorAction<TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_WITH_DUPLICATE_FAILED>;

export const changeSubscriptionUserInfoWithDuplicateFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): ChangeSubscriptionUserInfoWithDuplicateFailedAction => {
  const commonErrors = convertToCommonErrors(message, status, errors);
  return {
    errors: commonErrors,
    params,
    type: TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_WITH_DUPLICATE_FAILED,
  };
};

export interface SetDuplicateContactAction extends Action<TypeKeys.USE_DUPLICATE_CONTACT> {
  useDuplicateContact: GenericErrorDuplicateContact;
  type: TypeKeys.USE_DUPLICATE_CONTACT;
}

export const setDuplicateContact = (duplicateContact: GenericErrorDuplicateContact): SetDuplicateContactAction => ({
  useDuplicateContact: duplicateContact,
  type: TypeKeys.USE_DUPLICATE_CONTACT,
});

export interface ChangeSubscriptionPbxConfigurationAction
  extends CrudAction<TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION> {
  subscriptionId: string;
  pbxConfiguration: SubscriptionPbxConfiguration;
  validationFn: (pbxConfig: SubscriptionPbxConfiguration) => CommonError[] | undefined;
}

export const changeSubscriptionPbxConfiguration = (
  subscriptionId: string,
  pbxConfiguration: SubscriptionPbxConfiguration,
  validationFn: (pbxConfig: SubscriptionPbxConfiguration) => CommonError[] | undefined
): ChangeSubscriptionPbxConfigurationAction => ({
  id: generateActionId(),
  pbxConfiguration,
  subscriptionId,
  type: TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION,
  validationFn,
});

export interface ChangeSubscriptionPbxConfigurationFulfilledAction extends Action {
  subscriptionAction: SubscriptionAction;
  subscriptionId: string;
  type: TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FULFILLED;
}

export const changeSubscriptionPbxConfigurationFulfilled = (
  subscriptionAction: SubscriptionAction,
  subscriptionId: string
): ChangeSubscriptionPbxConfigurationFulfilledAction => ({
  subscriptionAction,
  subscriptionId,
  type: TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FULFILLED,
});

export type ChangeSubscriptionPbxConfigurationFailedAction =
  ErrorAction<TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FAILED>;

export const changeSubscriptionPbxConfigurationFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): ChangeSubscriptionPbxConfigurationFailedAction => {
  const commonErrors = convertToCommonErrors(message, status, errors);
  return {
    errors: commonErrors,
    params,
    type: TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FAILED,
  };
};

export interface SubscriptionAddOnAttributesMap {
  [addOnCode: string]: { [attributeName: string]: string };
}

export interface ChangeSubscriptionAddonsAction extends CrudAction<TypeKeys.CHANGE_ADDONS> {
  addOnsToAdd: string[];
  addOnsToRemove: string[];
  subscription: Subscription;
  addOnsToUpdate?: string[];
  addOnAttributes?: SubscriptionAddOnAttributesMap;
  type: TypeKeys.CHANGE_ADDONS;
}

export const changeSubscriptionAddons = (
  subscription: Subscription,
  addOnsToAdd: string[],
  addOnsToRemove: string[],
  addOnsToUpdate?: string[],
  addOnAttributes?: SubscriptionAddOnAttributesMap
): ChangeSubscriptionAddonsAction => ({
  addOnsToAdd,
  addOnsToRemove,
  addOnsToUpdate,
  addOnAttributes,
  id: generateActionId(),
  subscription,
  type: TypeKeys.CHANGE_ADDONS,
});

export interface ChangeSubscriptionAddonsFulfilledAction {
  addOnsToAdd: string[];
  addOnsToRemove: string[];
  subscriptionAction: SubscriptionAction;
  type: TypeKeys.CHANGE_ADDONS_FULFILLED;
}

export const changeSubscriptionAddonsFulfilled = (
  subscriptionAction: SubscriptionAction,
  addOnsToAdd: string[],
  addOnsToRemove: string[]
): ChangeSubscriptionAddonsFulfilledAction => ({
  addOnsToAdd,
  addOnsToRemove,
  subscriptionAction,
  type: TypeKeys.CHANGE_ADDONS_FULFILLED,
});

export type ChangeSubscriptionAddonsFailedAction = ErrorAction<TypeKeys.CHANGE_ADDONS_FAILED>;

export const changeSubscriptionAddonsFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): ChangeSubscriptionAddonsFailedAction => {
  const commonErrors = convertToCommonErrors(message, status, errors);
  return {
    errors: commonErrors,
    params,
    type: TypeKeys.CHANGE_ADDONS_FAILED,
  };
};

export interface InvalidBarringSelectionsAction extends Action {
  errorInfo: string;
  type: TypeKeys.BARRINGS_VALIDATION_FAILED;
}

// This is not reduced anywhere but the error catcher will send error event to GA
// because the action type ends with _FAILED
export const barringsValidationFailed = (errorInfo: string): InvalidBarringSelectionsAction => ({
  errorInfo,
  type: TypeKeys.BARRINGS_VALIDATION_FAILED,
});

export interface PostEppRedeemTerminateAction extends CrudAction<TypeKeys.POST_EPP_REDEEM_TERMINATE> {
  request: PostEppRedeemTerminateRequest;
  subscriptionId: string;
  personBillingAddress?: Address;
  personBillingEmail?: string;
  validationErrors?: CommonError[];
}

export interface PostEppRedeemTerminateFulfilledAction {
  request: PostEppRedeemTerminateRequest;
  response: SubscriptionAction;
  subscriptionId: string;
  type: TypeKeys.POST_EPP_REDEEM_TERMINATE_FULFILLED;
}

export type PostEppRedeemTerminateFailedAction = ErrorAction<TypeKeys.POST_EPP_REDEEM_TERMINATE_FAILED>;

export const postEppRedeemTerminateFulfilled = (
  request: PostEppRedeemTerminateRequest,
  response: SubscriptionAction,
  subscriptionId: string
): PostEppRedeemTerminateFulfilledAction => ({
  request,
  response,
  subscriptionId,
  type: TypeKeys.POST_EPP_REDEEM_TERMINATE_FULFILLED,
});

export const postEppRedeemTerminateFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): PostEppRedeemTerminateFailedAction => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.POST_EPP_REDEEM_TERMINATE_FAILED,
});

export interface LoadSubscriptionActions
  extends DisplayItemsLoadAction<TypeKeys.LOAD_SUBSCRIPTION_ACTIONS, SubscriptionAction> {
  forceLoad?: boolean;
  useSearchService?: boolean;
}

export interface LoadSubscriptionActionsFulfilled {
  subscriptionActionResponse: SubscriptionAction[];
  searchResults: SubscriptionActionSearchResponse[];
  useSearchService?: boolean;
  query: ItemsQuery;
  total: number;
  type: TypeKeys.LOAD_SUBSCRIPTION_ACTIONS_FULFILLED;
}

export type LoadSubscriptionActionsFailed = ErrorAction<TypeKeys.LOAD_SUBSCRIPTION_ACTIONS_FAILED>;

export const loadSubscriptionActions = (options?: { forceLoad?: boolean }): LoadSubscriptionActions => ({
  ...options,
  id: generateActionId(),
  type: TypeKeys.LOAD_SUBSCRIPTION_ACTIONS,
});

export const loadSubscriptionActionsFulfilled = (
  response: SubscriptionActionsResponse,
  query: ItemsQuery,
  useSearchService?: boolean
): LoadSubscriptionActionsFulfilled => ({
  subscriptionActionResponse: response.subscriptionActions,
  searchResults: response.searchResults,
  query,
  useSearchService,
  total: response.total,
  type: TypeKeys.LOAD_SUBSCRIPTION_ACTIONS_FULFILLED,
});

export const loadSubscriptionActionsFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): LoadSubscriptionActionsFailed => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.LOAD_SUBSCRIPTION_ACTIONS_FAILED,
});

export type SubscriptionActionsActionTypes =
  | InvalidBarringSelectionsAction
  | TerminateSubscriptionAction
  | TerminateSubscriptionActionFulfilled
  | TerminateSubscriptionActionFailed
  | ShowTerminateSubscriptionForm
  | CloseTerminateSubscriptionForm
  | ShowSuspendSubscriptionOptions
  | CloseSuspendSubscriptionOptions
  | SuspendSubscriptionAction
  | SuspendSubscriptionFailedAction
  | SuspendSubscriptionFulfilledAction
  | ResumeSubscriptionAction
  | ResumeSubscriptionFailedAction
  | ResumeSubscriptionFulfilledAction
  | CreateContactForSubscriptionUserInfoAction
  | CreateContactForSubscriptionUserInfoFulfilledAction
  | CreateContactForSubscriptionUserInfoFailedAction
  | ChangeSubscriptionUserInfoAction
  | ChangeSubscriptionUserInfoFailedAction
  | ChangeSubscriptionUserInfoWithDuplicateFailedAction
  | ChangeSubscriptionUserInfoFulfilledAction
  | EditSubscriptionSectionAction
  | ChangeSubscriptionPbxConfigurationAction
  | ChangeSubscriptionPbxConfigurationFailedAction
  | ChangeSubscriptionPbxConfigurationFulfilledAction
  | ChangeSubscriptionAddonsAction
  | ChangeSubscriptionAddonsFailedAction
  | ChangeSubscriptionAddonsFulfilledAction
  | PostEppRedeemTerminateAction
  | PostEppRedeemTerminateFailedAction
  | PostEppRedeemTerminateFulfilledAction
  | SetDuplicateContactAction
  | LoadSubscriptionActions
  | LoadSubscriptionActionsFailed
  | LoadSubscriptionActionsFulfilled;
